import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { translate } from "react-i18next";
import Immutable from "immutable";
import formValueSelector from "redux-form/lib/formValueSelector";
import getFormSyncErrors from "redux-form/lib/getFormSyncErrors";
import getFormAsyncErrors from "redux-form/lib/getFormAsyncErrors";
import Field from "redux-form/lib/Field";
import { createSelector } from "reselect";
import isEqual from "lodash/isEqual";

import SchemaField from "../../../component/SchemaField";

import StepBox from "../StepBox";

import "./index.scss";
import DiscountCodeField from "./DiscountCodeField";
import PaymentMethodField from "./PaymentMethodField";
import Overview from "../Overview";

/**
 * Step4
 */
class Index extends Component {
  static propTypes = {
    selectedPaymentMethodId: PropTypes.number,
    isActive: PropTypes.bool,
    isValid: PropTypes.bool,
    isOpen: PropTypes.bool,
    onNextStep: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]).isRequired,
    t: PropTypes.func.isRequired,
    hasOverview: PropTypes.bool,
    schema: PropTypes.instanceOf(Immutable.Map).isRequired,
    paymentMethods: PropTypes.instanceOf(Immutable.List).isRequired,
    isCompany: PropTypes.bool.isRequired,
    isBusinessAccount: PropTypes.bool.isRequired,
    children: PropTypes.node,
    step: PropTypes.number.isRequired
  };

  static defaultProps = {
    selectedPaymentMethodId: null,
    children: null,
    isActive: false,
    isValid: false,
    isOpen: false,
    isBusinessAccount: false,
    hasOverview: false
  };

  shouldComponentUpdate(nextProps) {
    // for some reason this does not work in the connect() "areMergedPropsEqual" option
    return !isEqual(this.props, nextProps);
  }

  render() {
    const { t, schema, paymentMethods } = this.props;
    const acPrefix = "section-checkout ";
    return (
      <StepBox
        number={this.props.step}
        title={this.props.t("Checkout")}
        nextStepBtnText={this.props.t("Checkout")}
        isActive={this.props.isActive}
        isValid={this.props.isValid}
        isOpen={this.props.isOpen}
        onNextStep={this.props.onNextStep}
        hasOverview={false}
      >
        <div className="Cart__Checkout">
          {this.props.selectedPaymentMethodId && (
            <div className="Cart__Checkout__PaymentMethod__Selected">{t("You have chosen")}</div>
          )}
          <Field
            name="checkout.payment.type"
            component={PaymentMethodField}
            schema={schema}
            paymentMethods={paymentMethods}
            parse={parseInt}
            t={t}
          />
          {this.props.selectedPaymentMethodId && (
            <div className="Cart__Checkout__PaymentMethod__Selector">
              <span
                className="btn btn-inverted"
                role="button"
                tabIndex="-1"
                onClick={this.onDisplayAllPaymentMethods}
                onKeyDown={this.onDisplayAllPaymentMethods}
              >
                {t("Choose a different payment method")}
              </span>
            </div>
          )}

          <div className="Cart__Checkout__DiscountCode row mt-3 mb-3">
            <div className="col-12">
              <DiscountCodeField name="checkout.payment.discountCode" schema={schema} className="pull-right" />
            </div>
          </div>

          {this.props.isCompany && (
            <div>
              <SchemaField
                name="checkout.payment.invoiceEmailAddress"
                schema={schema}
                tooltip={t("invoiceemailaddress.helptext")}
                autoComplete={`${acPrefix} email`}
              />
              <SchemaField name="checkout.payment.invoiceReference" schema={schema} tooltip={t("reference.helptext")} />
            </div>
          )}

          <div>
            <label htmlFor="form-control-checkout-comment">
              {schema.getIn(["properties", "checkout", "properties", "comment", "title"])}
            </label>
            <SchemaField
              name="checkout.comment"
              placeholder=" "
              schema={schema}
              label={false}
              autoComplete={`${acPrefix} comment`}
            />
          </div>

          {this.props.isOpen && this.props.hasOverview && <Overview key="cart_overview" />}

          {!this.props.isBusinessAccount && schema.getIn(['properties','checkout','properties','addToMailing']) && (
            <SchemaField name="checkout.addToMailing" schema={schema} unsafeLabel wrappedLabel />
          )}

          <SchemaField name="checkout.termsAndConditions" schema={schema} unsafeLabel wrappedLabel />

          {this.props.children}
        </div>
      </StepBox>
    );
  }
}

const valueSelector = formValueSelector("cart");

const selectCartContext = createSelector(
  [state => state.cart],
  cart => cart.get("context")
);

const selectIsBusinessAccount = createSelector(
  [state => selectCartContext(state)],
  context => context.get("isBusinessAccount", false)
);

const selectCartContextPaymentMethods = createSelector(
  [
    state => selectCartContext(state),
    () => null // parseInt(valueSelector(state, 'checkout.payment.type'), 10)
  ],
  (context, selectedPaymentMethodId) =>
    context.get("paymentMethods").filter(paymentMethod => {
      if (selectedPaymentMethodId && paymentMethod.get("id") !== selectedPaymentMethodId) {
        return false;
      }
      return true;
    })
);

// const selectSelectedPaymentMethod = createSelector(
//   [
//     state => selectCartContextPaymentMethods(state),
//     state => valueSelector(state, 'checkout.payment.type')
//   ],
//   (paymentMethods, selectedPaymentMethodId) =>
//     paymentMethods.find(v => v.get('id') === selectedPaymentMethodId)
// );

const schemaSelector = createSelector(
  [
    state => state.cart.get("schema"),
    state => selectCartContext(state),
    state => selectCartContextPaymentMethods(state)
  ],
  (schema, context, paymentMethods) => {
    const path = ["properties", "checkout", "properties", "payment", "properties", "type"];
    return schema
      .setIn(path.concat(["enum"]), paymentMethods.map(v => v.get("id")))
      .setIn(path.concat(["enum_titles"]), paymentMethods.map(v => v.get("title")));
  }
);

const formSyncErrorsSelector = getFormSyncErrors("cart");
const formAsyncErrorsSelector = getFormAsyncErrors("cart");
const paymentErrorSelector = createSelector(
  [state => formSyncErrorsSelector(state), state => formAsyncErrorsSelector(state)],
  (s, a) =>
    !!(
      (s && s.checkout && s.checkout.payment && s.checkout.payment.type) ||
      (a && a.checkout && a.checkout.payment && a.checkout.payment.type)
    )
);

export default translate()(
  connect(state => ({
    selectedPaymentMethodId: null, // valueSelector(state, 'checkout.payment.type'),
    paymentError: paymentErrorSelector(state),
    isCompany: valueSelector(state, "customer.type") === "company",
    schema: schemaSelector(state),
    paymentMethods: selectCartContextPaymentMethods(state),
    isBusinessAccount: selectIsBusinessAccount(state)
  }))(Index)
);
